import React, { useState, useContext, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import AppBarMenu from "../../components/AppBarMenu";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import ResourceList from "../../components/shared/ResourceList";
import IntegratorDrawer from "../../components/management/IntegratorDrawer";
import { ApiDataContext } from "../../contexts/ApiDataContext";
import { formatCPFOrCNPJ, formatPhone } from "../../utils/formatters";

/**
 * Página dedicada para gestão de integradores (clientes)
 * Usa ResourceList para listagem e IntegratorDrawer para criar/editar
 */
export default function IntegratorsPage() {
  const [searchParams] = useSearchParams();
  const { apiData } = useContext(ApiDataContext);
  
  // Estado do drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [createdClientId, setCreatedClientId] = useState(null);

  // Função de busca client-side (clientes vêm do ApiDataContext)
  const fetchClients = useCallback(async ({ page, limit, search }) => {
    const clients = apiData.clients || [];
    
    // Filtrar por search
    let filtered = clients;
    if (search && search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = clients.filter(client => 
        client.name?.toLowerCase().includes(searchLower) ||
        client.email?.toLowerCase().includes(searchLower) ||
        client.cpf?.includes(search) ||
        client.cnpj?.includes(search)
      );
    }

    // Paginar client-side (page começa em 1 no ResourceList)
    const start = (page - 1) * limit;
    const end = start + limit;
    const paginated = filtered.slice(start, end).map(client => ({
      ...client,
      _id: client.id // ResourceList espera _id
    }));

    return {
      data: paginated,
      pagination: {
        total: filtered.length,
        page,
        limit
      }
    };
  }, [apiData.clients]);

  // Colunas da tabela
  const columns = [
    { 
      header: "Nome", 
      accessor: "name",
      align: "left"
    },
    { 
      header: "Email", 
      accessor: "email",
      align: "left"
    },
    { 
      header: "CPF/CNPJ", 
      accessor: (item) => formatCPFOrCNPJ(item.cpf || item.cnpj),
      align: "left"
    },
    { 
      header: "Telefone", 
      accessor: (item) => formatPhone(item.phone),
      align: "left"
    }
  ];

  const handleCreateClient = () => {
    setDrawerMode("create");
    setSelectedClientId(null);
    setDrawerOpen(true);
  };

  const handleRowClick = (client) => {
    setDrawerMode("edit");
    setSelectedClientId(client.id);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setDrawerMode("create");
    setSelectedClientId(null);
    
    // Se criou um cliente, aguarda e fecha
    if (createdClientId) {
      setTimeout(() => {
        setCreatedClientId(null);
      }, 1000);
    }
  };

  return (
    <AppBarMenu>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <h1 style={{ margin: 0 }}>Integradores</h1>
            <p style={{ margin: "8px 0 0 0", color: "text.secondary" }}>
              Gerencie os integradores (clientes) cadastrados no sistema
            </p>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClient}
            sx={{ height: 40 }}
          >
            Novo Integrador
          </Button>
        </Box>

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <ResourceList
            fetchData={fetchClients}
            columns={columns}
            searchParam="search"
            onRowClick={handleRowClick}
            storageKey="integrators"
            resourceName="Integradores"
            initialSearch={searchParams.get('search') || ''}
          />
        </Paper>

        <IntegratorDrawer
          open={drawerOpen}
          onClose={handleCloseDrawer}
          mode={drawerMode}
          clientId={selectedClientId}
          onClientCreated={(clientId) => setCreatedClientId(clientId)}
        />
      </Container>
    </AppBarMenu>
  );
}
